import moment from 'moment';
import { useQuery } from 'react-query';

import validateTaxNumberRequest from '../../services/ValidateTaxNumber';

import { buildMomentFromDateString, hasAtLeastXDigits, isPortugalCountryId, isValidPtCitizenCardNumber, padZero, PORTUGAL_COUNTRY_ID, useDebouncedValue } from '../../utils/utils';
import { escapeRegExp, removeAccents } from '../../utils/string-utils';
import validatePostalCodeRequest from '../../services/ValidatePostalCode';
import { validateDriverAddress } from '../../services/ValidateDriverAddress';

export const MIN_DRIVER_LICENCE_PT_DIGITS = 1;

const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const MIN_DOCUMENT_ID_DIGITS = 1;
export function validateDocumentID(t, documentID, isPtCitizenCard) {
  if (!documentID) {
    return { isValid: false, message: t('emptyDocumentID') };
  }

  if (isPtCitizenCard) {
    return isValidPtCitizenCardNumber(documentID)
      ? { isValid: true }
      : { isValid: false, message: t('invalidDocumentIDPTCitizen') };
  }

  return hasAtLeastXDigits(documentID, MIN_DOCUMENT_ID_DIGITS)
    ? { isValid: true }
    : { isValid: false, message: t('invalidDocumentID') };
}

export function validateCountry(t, countryName, countries) {
  if (!countryName) {
    return { isValid: false, message: t('emptyCountry') };
  }
  const selectedCountry = countries.find(c => c.name.toLowerCase() === countryName.toLowerCase());
  if (!selectedCountry) {
    return { isValid: false, message: t('invalidCountry') };
  }
  return { isValid: true };
}

function isCoporporateNif(num) {
  return (num + '').startsWith('5') || (num + '').startsWith('6');
}

export function useValidateTaxNumber(t, taxNumber, blockedTaxNumbersList, countryID) {
  return useQuery(
    ['validateTaxNumber', taxNumber, 'country', countryID],
    async () => {
      if (!countryID) {
        return Promise.resolve({ isValid: false });
      }
      if (countryID && !isPortugalCountryId(countryID)) {
        return Promise.resolve({ isValid: true });
      }
      if (!taxNumber || taxNumber.length !== 9 || taxNumber === '999999999') {
        return Promise.resolve({ isValid: false, message: t('invalidTaxNumber') });
      }
      if (blockedTaxNumbersList.find(t => t === taxNumber)) {
        return Promise.resolve({ isValid: false, message: t('invalidTaxNumber') });
      }
      if (isCoporporateNif(taxNumber)) {
        return Promise.resolve({ isValid: false, message: t('invalidCorporateTaxNumber') });
      }

      const isValid = await validateTaxNumberRequest('33', taxNumber);
      return { isValid, message: isValid ? undefined : t('invalidTaxNumber') };
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    }
  );
}

export function validateNotEmpty(t, value, messageKey) {
  return value ? { isValid: true } : { isValid: false, message: t(messageKey) };
}

export function validateDate(t, day, month, year, messageKey) {
  const date = `${year}-${month}-${day}`;
  if (
    day &&
    month &&
    year &&
    Number.isInteger(Number(day)) &&
    Number.isInteger(Number(month)) &&
    Number.isInteger(Number(year)) &&
    year.toString().length === 4 &&
    buildMomentFromDateString(date).isValid() &&
    buildMomentFromDateString(date)
      .startOf('day')
      .isAfter(moment().startOf('day'))
  ) {
    return { isValid: true };
  }
  return { isValid: false, message: t(messageKey) };
}

export function isMaxDayToInput(day) {
  return !day ? false : Number(day) > 3 || day.length === 2;
}

export function isMaxMonthToInput(month) {
  return !month ? false : Number(month) > 1 || month.length === 2;
}

const COUNTRY_CODE_PREFIX = '+';

export function validatePhoneCountryCode(t, phoneCountryCode, countries) {
  const countriesPhoneCodes = countries
    .filter(c => c.PhoneCountryCode)
    .map(c => `${COUNTRY_CODE_PREFIX}${c.PhoneCountryCode}`);

  return phoneCountryCode && !countriesPhoneCodes.includes(phoneCountryCode.trim())
    ? { isValid: false, message: t('phoneCountryCodeInvalid') }
    : { isValid: true };
}

export function validateFullName(t, fullName, surname, firstName, differentReservationNameCounter, dispatch) {
  const resDriverNameParts = `${firstName} ${surname}`.split(' ').filter(v => v !== '');
  const matchesReservationName =
    resDriverNameParts.find(
      s => removeAccents(fullName).match(new RegExp(escapeRegExp(removeAccents(s)), 'gi')) === null
    ) === undefined;

  if (fullName && !matchesReservationName) {
    if (differentReservationNameCounter === 2) {
      dispatch({ type: 'SET_VALUE', payload: { errorModal: true } });
    }
    dispatch({ type: 'INCREMENT_DIFFERENT_RESERVATION_NAME_COUNTER' });
  }

  return !fullName
    ? { isValid: false, message: t('emptyFullName') }
    : matchesReservationName
    ? { isValid: true }
    : { isValid: false, message: t('fullNameReservationMismatch') };
}

export function validateTitle() {
  return { isValid: true };
}

export function validateEmail(t, email) {
  if (!email) {
    return { isValid: false, message: t('emptyEmail') };
  }
  return email.match(EMAIL_PATTERN) ? { isValid: true } : { isValid: false, message: t('invalidEmail') };
}

export function validateEmailConfirm(t, email, emailConfirm) {
  if (!emailConfirm) {
    return { isValid: false, message: t('emptyEmail') };
  }
  if (!emailConfirm.match(EMAIL_PATTERN)) {
    return { isValid: false, message: t('invalidEmail') };
  }
  if (email && emailConfirm && email.toUpperCase() !== emailConfirm.toUpperCase()) {
    return { isValid: false, message: t('invalidEmailsMatch') };
  }
  return { isValid: true };
}

function validPortugueseDriversLicence(number) {
  return /^[a-zA-Z]{1,2}-\d{1,7} \d{1}$/g.test(number);
}

export function validateDriversLicense(t, driversLicense, isPT) {
  return driversLicense
    ? {
        isValid: isPT
          ? validPortugueseDriversLicence(driversLicense)
          : hasAtLeastXDigits(driversLicense, MIN_DRIVER_LICENCE_PT_DIGITS),
        message: t('invalidDriversLicense', isPT ? { format: 'Ex: AA-1234567 8' } : {})
      }
    : { isValid: false, message: t('emptyDriversLicense') };
}

export function validateDriversLicenseIssuedDate(
  t,
  driversLicenseIssuedDay,
  driversLicenseIssuedMonth,
  driversLicenseIssuedYear,
  issuedDateOptional
) {
  if (
    issuedDateOptional &&
    !driversLicenseIssuedDay &&
    !driversLicenseIssuedMonth &&
    !driversLicenseIssuedYear
  ) {
    return { isValid: true };
  }
  if (
    driversLicenseIssuedDay &&
    driversLicenseIssuedMonth &&
    driversLicenseIssuedYear &&
    Number.isInteger(Number(driversLicenseIssuedDay)) &&
    Number.isInteger(Number(driversLicenseIssuedMonth)) &&
    Number.isInteger(Number(driversLicenseIssuedYear)) &&
    driversLicenseIssuedYear.toString().length === 4
  ) {
    const driversLicenseIssuedDate = `${driversLicenseIssuedYear}-${driversLicenseIssuedMonth}-${driversLicenseIssuedDay}`;
    if (moment().diff(driversLicenseIssuedDate, 'years') < 1) {
      return { isValid: false, message: t('notExperiencedEnoughToDrivre') };
    }
    return buildMomentFromDateString(driversLicenseIssuedDate).isValid()
      ? { isValid: true }
      : { isValid: false, message: t('invalidDriversLicenseIssuedDate') };
  }
  return { isValid: false, message: t('invalidDriversLicenseIssuedDate') };
}

export function validateDriversLicenseExpirationDate(
  t,
  driversLicenseExpirationDay,
  driversLicenseExpirationMonth,
  driversLicenseExpirationYear,
  expireDateOptional,
  driversLicenseIssuedDay,
  driversLicenseIssuedMonth,
  driversLicenseIssuedYear,
  issuedDateOptional,
  issuedDateValid,
) {
  if (
    expireDateOptional &&
    !driversLicenseExpirationDay &&
    !driversLicenseExpirationMonth &&
    !driversLicenseExpirationYear
  ) {
    return { isValid: true };
  }

  if (
    driversLicenseExpirationDay &&
    driversLicenseExpirationMonth &&
    driversLicenseExpirationYear &&
    Number.isInteger(Number(driversLicenseExpirationDay)) &&
    Number.isInteger(Number(driversLicenseExpirationMonth)) &&
    Number.isInteger(Number(driversLicenseExpirationYear)) &&
    driversLicenseExpirationYear.toString().length === 4
  ) {
    const driversLicenseExpirationDate = `${driversLicenseExpirationYear}-${driversLicenseExpirationMonth}-${driversLicenseExpirationDay}`;
    const driversLicenseIssuedDate = issuedDateOptional && !issuedDateValid ? null
      : `${driversLicenseIssuedYear}-${driversLicenseIssuedMonth}-${driversLicenseIssuedDay}`;

    if (driversLicenseIssuedDate  && buildMomentFromDateString(driversLicenseExpirationDate).isSameOrBefore(buildMomentFromDateString(driversLicenseIssuedDate))) {
      return { isValid: false, message: t('driversLicenseExpirationDateBeforeIssuedDate') };
    }
    const res =
      buildMomentFromDateString(driversLicenseExpirationDate).isValid() && buildMomentFromDateString(driversLicenseExpirationDate).isAfter(moment());
    return res ? { isValid: true } : { isValid: false, message: t('invalidDriversLicenseExpirationDate') };
  }
  return { isValid: false, message: t('invalidDriversLicenseExpirationDate') };
}

export function useValidatePostalCode(t, postalCode, countryID) {
  return useQuery(
    ['validatePostalCode', postalCode],
    async () => {
      if (!postalCode || postalCode.trim().length === 0) {
        return Promise.resolve({ isValid: false, message: t('emptyPostalCode') });
      }

      if (countryID === PORTUGAL_COUNTRY_ID) {
        return postalCode && postalCode.length === 8 && (await validatePostalCodeRequest(countryID, postalCode))
          ? Promise.resolve({ isValid: true })
          : Promise.resolve({
              isValid: false,
              message: t('invalidPostalCode')
            });
      }
      return Promise.resolve({ isValid: true });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000
    }
  );
}

export function useValidateAddress(t, address, doorNumber, skipAddressValidation) {
  const getInvalidAdressMessageType = message => {
    const types = {
      ["Falta Morada - campo em branco ou '0' ou '.'."]: 'emptyAddress',
      ['Morada incompleta - se não contiver no mínimo 5 letras  + 1 espaço em branco + 1 algarismo.']:
        'invalidAddressIncomplete',
      ['Falta nº de Porta - Tem morada mas não contém algarismos.']: 'invalidAddressDoorNumber'
    };
    return types[message] || 'InvalidAddressResponse';
  };

  const addressFormatted = doorNumber ? `${address} ${doorNumber}` : address
  const debouncedAddress = useDebouncedValue(addressFormatted, 800)

  return useQuery(
    ['validateAddress', debouncedAddress, skipAddressValidation],
    async () => {
      if (skipAddressValidation) {
        return Promise.resolve({ isValid: true });
      }

      if (!address) {
        return Promise.resolve({ isValid: false, message: t('emptyAddress') });
      }

      const validateAddressRes = await validateDriverAddress(addressFormatted);
      if (validateAddressRes.hasError) {
        const messageId = getInvalidAdressMessageType(validateAddressRes.message);
        const missingDoorNumberAlert = !doorNumber && !/\d/.test(address) && messageId === 'invalidAddressIncomplete';
        if (missingDoorNumberAlert) {
          return { isValid: false, invalidDoorNumberAlert: true, message: t(messageId) };
        }
        return { isValid: false,  invalidAddressAlert: true, message: t(messageId) };
      }
      return { isValid: true };
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000
    }
  );
}

export function validateCity(t, city, countryID) {
  if (countryID === PORTUGAL_COUNTRY_ID) {
    return { isValid: true };
  }
  return city ? { isValid: true } : { isValid: false, message: t('emptyCity') };
}

export function additionalDriverFieldsPresent(values) {
  return values && values.fullName && values.birthDate && values.driversLicenseIssuedPlace && values.driversLicense
    && values.driversLicenseIssuedDate && values.driversLicenseExpirationDate
}
